<template>
  <section class="promotions-page">
    <div class="bb-container">
      <div class="back">
        <div class="back__back">
          <a @click.prevent="$router.back()">
            <div class="back__name">
              <img
                src="../../assets/img/ico/ico-arrow-back.svg"
                loading="lazy"
                :alt="$t('generic.goback')"
                class="back__arrow"
              />
            </div>
          </a>
        </div>
      </div>

      <div class="promotions-content">
        <div class="promotions-page__header">
          <h1 class="promotions-page__title">
            <template v-if="retailerFromRoute">
              {{ retailerDisplayName }}
            </template>
            <template v-else>
              🔥 {{ $t("deals.pageTitle") }}
            </template>
          </h1>
          <p v-if="retailerFromRoute" class="promotions-page__subtitle">
            {{ filteredPromotions.length }} {{ filteredPromotions.length === 1 ? $t("deals.availableOffer") : $t("deals.availableOffers") }}
          </p>
        </div>

        <!-- Cercador de text lliure -->
        <!-- <div class="promotions-page__search">
          <div class="search-wrapper">
            <input 
              v-model="searchQuery"
              type="text"
              :placeholder="$t('deals.searchPlaceholder')"
              class="search-input"
            />
            <span class="search-icon"></span>
          </div>
        </div> -->

        <!-- Llista de promocions -->
        <div v-if="filteredPromotions.length > 0" class="promotions-page__list">
          <promotion-card
            v-for="promotion in filteredPromotions"
            :key="promotion.id"
            :promotion="promotion"
          />
        </div>

        <!-- Estat buit -->
        <div v-else-if="!loading" class="promotions-page__empty">
          <div class="empty-state">
            <span class="empty-state__icon">{{ searchQuery ? '🔍' : '🎁' }}</span>
            <h2 class="empty-state__title">
              {{ searchQuery 
                ? ($t("mylist.deals.noResults") || "No se encontraron promociones") 
                : ($t("mylist.deals.noPromotions") || "No hay promociones activas") 
              }}
            </h2>
            <p class="empty-state__text">
              {{ searchQuery
                ? ($t("mylist.deals.tryDifferentSearch") || "Intenta buscar con otras palabras")
                : ($t("mylist.deals.noPromotionsText") || "Cuando haya ofertas disponibles, las verás aquí.")
              }}
            </p>
          </div>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="promotions-page__loading">
          <div class="spinner"></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import PromotionCard from "../../components/promotions/PromotionCard.vue";

export default {
  name: "PromotionsListPage",
  components: {
    PromotionCard,
  },
  data() {
    return {
      promotions: [],
      searchQuery: '',
      loading: true,
    };
  },
  computed: {
    ...mapGetters({
      user: "user",
      userMarket: "loggedInUserMarket",
    }),
    
    retailerFromRoute() {
      return this.$route.params.retailer || null;
    },
    
    retailerDisplayName() {
      if (!this.retailerFromRoute) return '';
      
      // Buscar el retailer a les promocions per obtenir el nom formatat
      const promo = this.promotions.find(p => p.retailerCode === this.retailerFromRoute);
      if (promo) {
        return this.formatRetailerName(promo.retailerName);
      }
      
      return this.formatRetailerName(this.retailerFromRoute);
    },
    
    filteredPromotions() {
      let filtered = this.promotions;
      
      // Filtrar per retailer si ve de la ruta
      if (this.retailerFromRoute) {
        filtered = filtered.filter(p => p.retailerCode === this.retailerFromRoute);
      }
      
      // Filtrar per cerca de text
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(promo => {
          return (
            promo.title?.toLowerCase().includes(query) ||
            promo.description?.toLowerCase().includes(query) ||
            promo.retailerName?.toLowerCase().includes(query) ||
            promo.couponCode?.toLowerCase().includes(query)
          );
        });
      }
      
      // Ordenar: primer les que expiren aviat, després per ID descendent
      return filtered.sort((a, b) => {
        if (!a.endAt && !b.endAt) return b.id - a.id;
        if (!a.endAt) return 1;
        if (!b.endAt) return -1;
        
        const dateCompare = new Date(a.endAt) - new Date(b.endAt);
        return dateCompare !== 0 ? dateCompare : b.id - a.id;
      });
    },
  },
  methods: {
    formatRetailerName(name) {
      const retailers = {
        'elcorteingles': 'El Corte Inglés',
        'momcozy': 'Momcozy',
        'amazon': 'Amazon',
        'amazontest': 'Amazon Test',
        'decathlon': 'Decathlon',
        'vertbaudet': 'Vertbaudet',
        'bebitus': 'Bebitus',
      };
      return retailers[name] || this.$utils?.capitalize(name) || name;
    },
    
    async loadPromotions() {
      this.loading = true;
      
      try {
        const response = await this.$store.dispatch('getAllPromotions', {
          market: this.userMarket || 'ES',
        });
        
        this.promotions = Array.isArray(response) ? response : [];
      } catch (error) {
        console.error('Error carregant promocions:', error);
        this.promotions = [];
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    await this.loadPromotions();
  },
};
</script>

<style scoped>
.promotions-page {
  padding: 24px 0;
  min-height: 60vh;
}

.promotions-content {
  max-width: 800px;
  margin: 0 auto;
}

.promotions-page__header {
  margin-bottom: 24px;
}

.promotions-page__title {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.promotions-page__subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 8px 0 0 0;
  font-weight: 500;
}

.promotions-page__search {
  margin-bottom: 24px;
}

.search-wrapper {
  position: relative;
  width: 100%;
}

.search-input {
  width: 100%;
  padding: 12px 48px 12px 16px;
  font-size: 16px;
  border: 1px solid #e4e4e4;
  border-radius: 50px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  border-color: #d7d7d7;
  color: #262626;
}

.search-input:focus {
  outline: none;
  border: 2px solid #f97316;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  padding: 12px 48px 12px 15px;
}

.search-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  background-image: url(../../assets/img/ico/ico-search.svg);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  pointer-events: none;
  opacity: 0.5;
}

.promotions-page__list {
  /* No cal max-width aquí perquè ja està al contenidor pare */
}

.promotions-page__empty {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.empty-state {
  text-align: center;
  max-width: 400px;
}

.empty-state__icon {
  font-size: 64px;
  display: block;
  margin-bottom: 16px;
}

.empty-state__title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;
}

.empty-state__text {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.promotions-page__loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top-color: #f97316;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 767px) {
  .promotions-page__title {
    font-size: 24px;
  }
  
  .promotions-page__subtitle {
    font-size: 14px;
  }
  
  .search-input {
    font-size: 14px;
  }
}
</style>
